import React, { useState, useEffect } from 'react';
import { useMarketData } from '../contexts/MarketDataContext';

interface Trade {
  id: string;
  price: number;
  amount: number;
  time: Date;
  isBuyer: boolean;
}

interface RecentTradesProps {
  selectedPair: string;
  quoteCurrency?: 'USD' | 'USDT';
}

const RecentTrades: React.FC<RecentTradesProps> = ({ selectedPair, quoteCurrency = 'USDT' }) => {
  const { getPriceBySymbol } = useMarketData();
  const currentPrice = getPriceBySymbol(selectedPair);
  const [state, setState] = useState<{ symbol: string; trades: Trade[] }>({ symbol: selectedPair, trades: [] });
  const quoteAvailable = Number.isFinite(currentPrice) && currentPrice > 0;
  const trades = state.symbol === selectedPair && quoteAvailable ? state.trades : [];

  useEffect(() => {
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
      setState({ symbol: selectedPair, trades: [] });
      return;
    }
    const generateInitialTrades = () => {
      const initialTrades: Trade[] = [];
      const now = new Date();

      for (let i = 0; i < 30; i++) {
        const priceVariation = (Math.random() - 0.5) * 0.002;
        const tradePrice = currentPrice * (1 + priceVariation);

        initialTrades.push({
          id: `trade-${Date.now()}-${i}`,
          price: tradePrice,
          amount: Math.random() * 2 + 0.001,
          time: new Date(now.getTime() - i * 2000),
          isBuyer: Math.random() > 0.5
        });
      }

      return initialTrades;
    };
    setState(previous => previous.symbol === selectedPair && previous.trades.length > 0
      ? previous : { symbol: selectedPair, trades: generateInitialTrades() });

    const interval = setInterval(() => {
      const priceVariation = (Math.random() - 0.5) * 0.001;
      const tradePrice = currentPrice * (1 + priceVariation);

      const newTrade: Trade = {
        id: `trade-${Date.now()}`,
        price: tradePrice,
        amount: Math.random() * 2 + 0.001,
        time: new Date(),
        isBuyer: Math.random() > 0.5
      };

      setState(previous => ({ symbol: selectedPair,
        trades: [newTrade, ...(previous.symbol === selectedPair ? previous.trades.slice(0, 49) : [])] }));
    }, 500 + Math.random() * 1500);

    return () => clearInterval(interval);
  }, [selectedPair, currentPrice]);

  const formatPrice = (price: number) => {
    const isJpyPair = selectedPair.includes('JPY');
    const isForexPair = selectedPair.includes('EUR') || selectedPair.includes('GBP') || selectedPair.includes('AUD') ||
      selectedPair.includes('NZD') || selectedPair.includes('CAD') || selectedPair.includes('CHF') ||
      (selectedPair.includes('USD') && !selectedPair.includes('USDT') && !selectedPair.endsWith('/USD'));

    if (isJpyPair) return price.toFixed(3);
    if (isForexPair || (price >= 0.1 && price < 10)) return price.toFixed(5);
    if (price >= 10000) return price.toFixed(2);
    if (price >= 1000) return price.toFixed(2);
    if (price >= 100) return price.toFixed(3);
    if (price >= 10) return price.toFixed(4);
    if (price >= 1) return price.toFixed(5);
    return price.toFixed(6);
  };

  const formatAmount = (amount: number) => {
    return amount.toFixed(5);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col bg-white" translate="no">
      <div className="grid grid-cols-3 text-xs font-medium p-3 border-b border-slate-200 sticky top-0 bg-white z-10">
        <div className="text-slate-500">Price ({quoteCurrency})</div>
        <div className="text-right text-slate-500">Amount</div>
        <div className="text-right text-slate-500">Time</div>
      </div>

      <div className="flex-1 overflow-auto">
        {trades.map((trade, index) => (
          <div
            key={trade.id}
            className={`grid grid-cols-3 text-xs p-2 transition-colors ${
              index === 0 ? 'bg-slate-50' : ''
            }`}
          >
            <div className={`font-mono ${trade.isBuyer ? 'text-green-500' : 'text-red-500'}`}>
              {formatPrice(trade.price)}
            </div>
            <div className="text-right font-mono text-slate-600">
              {formatAmount(trade.amount)}
            </div>
            <div className="text-right font-mono text-slate-400">
              {formatTime(trade.time)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentTrades;
